import { Box, Flex, IconButton, Image, Stack, Text } from '@chakra-ui/react';
import { ManualOverride, OcrItemDto } from '@diamond/shared/types';
import { TextField } from '@diamond/shared/ui';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { KeyboardEvent } from 'react';
import { useFormContext } from 'react-hook-form';

import { AnalyzingChip } from './AnalyzingChip';
import { formatTransactionDatetime } from './formatTransactionDatetime';
import { ManualFallbackFields } from './ManualFallbackFields';

interface OcrPreviewCardProps {
  item: OcrItemDto;
  state:
    | 'analyzing'
    | 'success'
    | 'needs_account_name'
    | 'manual_fallback'
    | 'duplicate'
    | 'blocked';
  fieldIndex: number;
  onDelete: () => void;
  onConfirmAccountName?: () => void;
  senderAccountNameOverride?: string;
  isDeleting?: boolean;
}

function OcrSuccessBlock({
  item,
  includeOwnerName = true,
  senderAccountNameOverride,
}: {
  item: OcrItemDto;
  includeOwnerName?: boolean;
  senderAccountNameOverride?: string;
}) {
  const amount =
    item.ocr_result?.amount != null
      ? buyerCurrencyFormatter(item.ocr_result.amount)
      : '-';
  const recipientName = item.ocr_result?.recipient_name ?? '-';
  const senderBank = item.ocr_result?.sender_bank ?? '-';
  const senderAccountName =
    senderAccountNameOverride ?? item.ocr_result?.sender_account_name ?? '-';
  const transactionDate = formatTransactionDatetime(
    item.ocr_result?.transaction_datetime
  );

  const rows: Array<{ label: string; value: string }> = [
    { label: 'Nominal', value: amount },
    { label: 'Penerima', value: recipientName },
    { label: 'Tanggal Transfer', value: transactionDate },
    { label: 'Bank Pengirim', value: senderBank },
  ];

  if (includeOwnerName) {
    rows.push({ label: 'Nama Pemilik Rekening', value: senderAccountName });
  }

  return (
    <Stack spacing="12px">
      {rows.map(({ label, value }) => (
        <Box key={label}>
          <Text
            fontSize="10px"
            fontWeight={400}
            color="#373f41"
            lineHeight="12px"
          >
            {label}
          </Text>
          <Text
            fontSize="14px"
            fontWeight={600}
            color="#373f41"
            lineHeight="17px"
            mt="4px"
          >
            {value}
          </Text>
        </Box>
      ))}
    </Stack>
  );
}

function AccountNameConfirmationField({
  fieldIndex,
  onConfirmAccountName,
}: {
  fieldIndex: number;
  onConfirmAccountName?: () => void;
}) {
  const {
    register,
    formState: { errors },
  } = useFormContext<{ manual_overrides: ManualOverride[] }>();
  const nameFieldName =
    `manual_overrides.${fieldIndex}.sender_account_name` as const;

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    onConfirmAccountName?.();
  };

  return (
    <Box maxW="170px">
      <TextField
        name={nameFieldName}
        label="Nama Bank Pemilik"
        register={register}
        errors={errors}
        placeholder="Tulis disini"
        bg="white"
        border="1px solid"
        borderColor="gray.33"
        borderRadius="8px"
        fontSize="16px"
        h="40px"
        lineHeight="24px"
        maxLength={100}
        onKeyDown={handleKeyDown}
        _placeholder={{ color: '#aeaeb2' }}
        labelProps={{
          color: '#373f41',
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '17px',
          mb: '8px',
        }}
      />
    </Box>
  );
}

export const OcrPreviewCard = ({
  item,
  state,
  fieldIndex,
  onDelete,
  onConfirmAccountName,
  senderAccountNameOverride,
  isDeleting = false,
}: OcrPreviewCardProps) => {
  function renderStateContent() {
    if (state === 'analyzing') {
      return <AnalyzingChip />;
    }

    if (state === 'success') {
      return (
        <OcrSuccessBlock
          item={item}
          senderAccountNameOverride={senderAccountNameOverride}
        />
      );
    }

    if (state === 'needs_account_name') {
      return (
        <Stack spacing="12px">
          <OcrSuccessBlock item={item} includeOwnerName={false} />
          <AccountNameConfirmationField
            fieldIndex={fieldIndex}
            onConfirmAccountName={onConfirmAccountName}
          />
        </Stack>
      );
    }

    if (state === 'manual_fallback') {
      return (
        <Stack spacing={4}>
          {item.ocr_result !== null && <OcrSuccessBlock item={item} />}
          <ManualFallbackFields fieldIndex={fieldIndex} />
        </Stack>
      );
    }

    if (state === 'duplicate') {
      return (
        <Text fontSize="12px" color="red.500" fontWeight={500}>
          Bukti transfer sudah pernah digunakan
        </Text>
      );
    }

    if (state === 'blocked') {
      return (
        <Text fontSize="12px" color="red.500" fontWeight={500}>
          {item.display_error?.message ?? 'Bukti transfer tidak dapat diproses'}
        </Text>
      );
    }

    return null;
  }

  return (
    <Box bg="#f1f8ff" borderRadius="0" p="16px">
      <Flex gap="12px" align="flex-start">
        <Box flexShrink={0}>
          <Image
            src={item.preview_url}
            alt={item.file_name}
            w="120px"
            h="120px"
            borderRadius="4px"
            objectFit="cover"
            fallback={
              <Box bg="gray.100" w="120px" h="120px" borderRadius="4px" />
            }
          />
        </Box>

        <Stack flex={1} maxW="170px" minW={0} spacing={3}>
          <Flex justify="space-between" align="flex-start" gap="8px">
            <Text
              fontSize="14px"
              fontWeight={400}
              color="#373f41"
              lineHeight="20px"
              noOfLines={2}
              wordBreak="normal"
              flex={1}
            >
              {item.file_name}
            </Text>
            <IconButton
              aria-label="Hapus bukti transfer"
              icon={<DeleteOutline sx={{ fontSize: 22 }} />}
              variant="ghost"
              size="sm"
              color="#A0A0A0"
              bg="#FAFAFA"
              minW="32px"
              w="32px"
              h="32px"
              p="4px"
              flexShrink={0}
              _active={{ bgColor: 'white' }}
              _hover={{ bgColor: 'gray.33' }}
              onClick={onDelete}
              isLoading={isDeleting}
            />
          </Flex>

          {renderStateContent()}
        </Stack>
      </Flex>
    </Box>
  );
};
